import type { ProjectStatus, OcrStatus, AttachmentRole, AttachmentType } from './pocketbase'

/**
 * Project status labels for Simple and Pro modes (Thai)
 */
export const PROJECT_STATUS_LABELS: Record<
  ProjectStatus,
  { simple: string; pro: string; icon: string }
> = {
  'tor-prep': {
    simple: 'จัดทำ TOR',
    pro: 'จัดทำ TOR',
    icon: 'solar:document-text-line-duotone',
  },
  'purchase-request': {
    simple: 'รายงานขอซื้อขอจ้าง',
    pro: 'รายงานขอซื้อขอจ้าง',
    icon: 'solar:clipboard-list-line-duotone',
  },
  'invitation-issued': {
    simple: 'ประกาศเชิญชวน',
    pro: 'ประกาศเชิญชวน',
    icon: 'solar:letter-opened-line-duotone',
  },
  'award-approved': {
    simple: 'อนุมัติผู้ชนะ',
    pro: 'อนุมัติผู้ชนะ',
    icon: 'solar:cup-star-line-duotone',
  },
  'contract-management': {
    simple: 'บริหารสัญญา',
    pro: 'บริหารสัญญา',
    icon: 'solar:document-medicine-line-duotone',
  },
  'project-cancelled': {
    simple: 'ยกเลิกโครงการ',
    pro: 'ยกเลิกโครงการ',
    icon: 'solar:close-circle-line-duotone',
  },
}

/**
 * OCR status labels (Thai)
 */
export const OCR_STATUS_LABELS: Record<OcrStatus, { label: string; icon: string }> = {
  pending: { label: 'รอดำเนินการ', icon: 'solar:clock-circle-line-duotone' },
  processing: { label: 'กำลังประมวลผล', icon: 'solar:refresh-circle-line-duotone' },
  done: { label: 'เสร็จสิ้น', icon: 'solar:check-circle-line-duotone' },
  failed: { label: 'ล้มเหลว', icon: 'solar:danger-circle-line-duotone' },
}

/**
 * Attachment role labels (Thai)
 */
export const ATTACHMENT_ROLE_LABELS: Record<AttachmentRole, string> = {
  'invitation-notice': 'ประกาศเชิญชวน',
  'bidding-doc': 'เอกสารประกวดราคา',
  'tor-draft': 'ร่าง TOR',
  'median-price': 'ราคากลาง',
  'contract-essentials': 'สาระสำคัญในสัญญา',
  'change-notice': 'ประกาศเปลี่ยนแปลง',
  'cancel-notice': 'ประกาศยกเลิก',
  combo: 'เอกสารรวม',
  'initial-bid-summary': 'สรุปข้อเสนอเบื้องต้น',
  'quarterly-report': 'รายงานรายไตรมาส',
  supporting: 'เอกสารประกอบ',
  screenshot: 'ภาพหน้าจอ',
  other: 'อื่นๆ',
}

/**
 * Attachment type labels (Thai)
 */
export const ATTACHMENT_TYPE_LABELS: Record<AttachmentType, string> = {
  'median-price-announcement': 'ประกาศราคากลาง',
  'tor-draft-announcement': 'ประกาศร่าง TOR',
  'invitation-announcement': 'ประกาศเชิญชวน',
  'winner-announcement': 'ประกาศผู้ชนะการเสนอราคา',
  'contract-essentials': 'สาระสำคัญในสัญญา',
  'winner-change-notice': 'เปลี่ยนแปลงประกาศผู้ชนะฯ',
  'invitation-cancel': 'ยกเลิกประกาศเชิญชวน',
  'winner-cancel': 'ยกเลิกประกาศผู้ชนะฯ',
  'invitation-combo': 'ประกาศเชิญชวน (รวม)',
  'winner-combo': 'ประกาศผู้ชนะ (รวม)',
  'initial-bid-summary': 'สรุปข้อมูลการเสนอราคาเบื้องต้น',
  'quarterly-award-report': 'ประกาศผลผู้ชนะรายไตรมาส',
}

/**
 * Procurement method labels (Thai)
 */
export const PROCUREMENT_METHOD_LABELS: Record<string, string> = {
  'e-market': 'ตลาดอิเล็กทรอนิกส์ (e-market)',
  'e-bidding': 'ประกวดราคาอิเล็กทรอนิกส์ (e-bidding)',
  selective: 'คัดเลือก',
  specific: 'เฉพาะเจาะจง',
  'consult-open-call': 'จ้างที่ปรึกษา (ประกาศเชิญชวนทั่วไป)',
  'consult-selective-a': 'จ้างที่ปรึกษา (คัดเลือก)',
  'consult-selective-b': 'จ้างที่ปรึกษา (คัดเลือก)',
  'consult-specific': 'จ้างที่ปรึกษา (เฉพาะเจาะจง)',
  'consult-negotiation': 'จ้างที่ปรึกษา (ตกลง)',
  'design-supervision-open': 'จ้างออกแบบและควบคุมงาน (ประกาศฯ)',
  'design-supervision-selective': 'จ้างออกแบบและควบคุมงาน (คัดเลือก)',
  'design-supervision-specific': 'จ้างออกแบบและควบคุมงาน (เฉพาะเจาะจง)',
  'design-supervision-competition': 'จ้างออกแบบและควบคุมงาน (ประกวดแบบ)',
  'design-negotiation': 'จ้างออกแบบ (ตกลง)',
  'design-selective': 'จ้างออกแบบ (คัดเลือก)',
  'design-selective-limited': 'จ้างออกแบบ (คัดเลือกแบบจำกัด)',
  'design-special': 'จ้างออกแบบ (พิเศษ)',
  'design-special-competition': 'จ้างออกแบบ (พิเศษประกวดแบบ)',
  'international-bidding': 'ประกวดราคานานาชาติ',
  'price-negotiation': 'ตกลงราคา',
  'price-survey': 'สอบราคา',
  'general-bidding': 'ประกวดราคา',
  special: 'พิเศษ',
  'special-case': 'กรณีพิเศษ',
  'e-bidding-market-provider': 'ประกวดราคาอิเล็กทรอนิกส์ (ผ่านผู้ให้บริการตลาดกลาง)',
}

/**
 * Procurement type labels (Thai)
 */
export const PROCUREMENT_TYPE_LABELS: Record<string, string> = {
  purchase: 'ซื้อ',
  'construction-contract': 'จ้างก่อสร้าง',
  'service-contract': 'จ้างทำของ/จ้างเหมาบริการ',
  rental: 'เช่า',
  consulting: 'จ้างที่ปรึกษา',
  design: 'จ้างออกแบบ',
  supervision: 'จ้างควบคุมงาน',
  'design-supervision': 'จ้างออกแบบและควบคุมงาน',
}

/**
 * Thai province names by code
 */
export const PROVINCE_NAMES: Record<string, string> = {
  krabi: 'กระบี่',
  bangkok: 'กรุงเทพมหานคร',
  kanchanaburi: 'กาญจนบุรี',
  kalasin: 'กาฬสินธุ์',
  'kamphaeng-phet': 'กำแพงเพชร',
  'khon-kaen': 'ขอนแก่น',
  chanthaburi: 'จันทบุรี',
  chachoengsao: 'ฉะเชิงเทรา',
  chonburi: 'ชลบุรี',
  chainat: 'ชัยนาท',
  chaiyaphum: 'ชัยภูมิ',
  chumphon: 'ชุมพร',
  'chiang-rai': 'เชียงราย',
  'chiang-mai': 'เชียงใหม่',
  trang: 'ตรัง',
  trat: 'ตราด',
  tak: 'ตาก',
  'nakhon-nayok': 'นครนายก',
  'nakhon-pathom': 'นครปฐม',
  'nakhon-phanom': 'นครพนม',
  'nakhon-ratchasima': 'นครราชสีมา',
  'nakhon-si-thammarat': 'นครศรีธรรมราช',
  'nakhon-sawan': 'นครสวรรค์',
  nonthaburi: 'นนทบุรี',
  narathiwat: 'นราธิวาส',
  nan: 'น่าน',
  'bueng-kan': 'บึงกาฬ',
  'buri-ram': 'บุรีรัมย์',
  'pathum-thani': 'ปทุมธานี',
  'prachuap-khiri-khan': 'ประจวบคีรีขันธ์',
  'prachin-buri': 'ปราจีนบุรี',
  pattani: 'ปัตตานี',
  'phra-nakhon-si-ayutthaya': 'พระนครศรีอยุธยา',
  phayao: 'พะเยา',
  'phang-nga': 'พังงา',
  phatthalung: 'พัทลุง',
  phichit: 'พิจิตร',
  phitsanulok: 'พิษณุโลก',
  phetchaburi: 'เพชรบุรี',
  phetchabun: 'เพชรบูรณ์',
  phrae: 'แพร่',
  phuket: 'ภูเก็ต',
  'maha-sarakham': 'มหาสารคาม',
  mukdahan: 'มุกดาหาร',
  'mae-hong-son': 'แม่ฮ่องสอน',
  yasothon: 'ยโสธร',
  yala: 'ยะลา',
  'roi-et': 'ร้อยเอ็ด',
  ranong: 'ระนอง',
  rayong: 'ระยอง',
  ratchaburi: 'ราชบุรี',
  'lop-buri': 'ลพบุรี',
  lampang: 'ลำปาง',
  lamphun: 'ลำพูน',
  loei: 'เลย',
  'si-sa-ket': 'ศรีสะเกษ',
  'sakon-nakhon': 'สกลนคร',
  songkhla: 'สงขลา',
  satun: 'สตูล',
  'samut-prakan': 'สมุทรปราการ',
  'samut-songkhram': 'สมุทรสงคราม',
  'samut-sakhon': 'สมุทรสาคร',
  'sa-kaeo': 'สระแก้ว',
  saraburi: 'สระบุรี',
  'sing-buri': 'สิงห์บุรี',
  sukhothai: 'สุโขทัย',
  'suphan-buri': 'สุพรรณบุรี',
  'surat-thani': 'สุราษฎร์ธานี',
  surin: 'สุรินทร์',
  'nong-khai': 'หนองคาย',
  'nong-bua-lamphu': 'หนองบัวลำภู',
  'ang-thong': 'อ่างทอง',
  'amnat-charoen': 'อำนาจเจริญ',
  'udon-thani': 'อุดรธานี',
  uttaradit: 'อุตรดิตถ์',
  'uthai-thani': 'อุทัยธานี',
  'ubon-ratchathani': 'อุบลราชธานี',
}

/**
 * Budget years (BE) available for filtering
 */
export const BUDGET_YEARS = Array.from({ length: 15 }, (_, i) => (2567 + i).toString())

/**
 * Default pagination settings
 */
export const DEFAULT_PAGE_SIZE = 20
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
